/**
 * `/sheets` search params → J1 / J2 query params. 13-ui-screens.md §6.6.
 *
 * WHY A HAND-WRITTEN `validateSearch` AND NOT THE ZOD SCHEMA IN §6.6:
 * `zod` is a dependency of `@cropmate/shared`, NOT of `@cropmate/web`
 * (verified: `apps/web/package.json` has no zod, and pnpm's strict layout means
 * `import { z } from 'zod'` would not resolve from this package). Adding it is
 * out of scope for this task, so the schema is transcribed as a plain narrowing
 * function with the same semantics — every `.catch(x)` becomes a fallback, every
 * `.optional()` becomes `undefined`. `/_app/login` already does exactly this.
 *
 * The four chips are UI words (`all|entered|incomplete|missing`); the wire has
 * two booleans (`hasJobs`, `incomplete`) and a two-member `JobDayStatus`
 * (`NOT_STARTED|ENTERED`) since ADR-0012. They are NOT the same vocabulary and
 * the mapping lives here, once — see `sheetsListQuery`.
 */
import type { QueryParams } from '@/api/client';

/** The four filter chips of §6.1, in the order they are rendered. */
export const SHEET_STATUS_FILTERS = ['all', 'entered', 'incomplete', 'missing'] as const;
export type SheetStatusFilter = (typeof SHEET_STATUS_FILTERS)[number];

/** §6.4 — server-side pagination, fixed page size, no size changer. */
export const SHEETS_PAGE_SIZE = 25;

export interface SheetsSearch {
  status: SheetStatusFilter;
  page: number;
  /** Team UUID. Absent = every team. */
  team?: string;
  /** Absent = the server picks the open pay period (J1 `resolveRange`). */
  from?: string;
  to?: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function asFilter(value: unknown): SheetStatusFilter {
  return SHEET_STATUS_FILTERS.find((s) => s === value) ?? 'all';
}

function asPage(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}

function asMatch(value: unknown, re: RegExp): string | undefined {
  return typeof value === 'string' && re.test(value) ? value : undefined;
}

/** Mirrors the zod schema in §6.6, `.catch()` semantics included. */
export function validateSheetsSearch(raw: Record<string, unknown>): SheetsSearch {
  return {
    status: asFilter(raw['status']),
    page: asPage(raw['page']),
    team: asMatch(raw['team'], UUID),
    from: asMatch(raw['from'], ISO_DATE),
    to: asMatch(raw['to'], ISO_DATE),
  };
}

/**
 * J1 `GET /job-days`.
 *
 * There is no `?status=` any more (ADR-0012). Each chip is one of J1's two booleans, and each
 * is the SAME predicate the summary card beside it counts with, so chip and card agree:
 *   · `Entered`    — `hasJobs=true`     ↔ `sheetsEntered`
 *   · `Incomplete` — `incomplete=true`  ↔ `sheetsIncomplete` (and P7's refusal)
 *   · `No entry`   — `hasJobs=false`    ↔ `sheetsMissing` (`d.id IS NULL OR no job_line`)
 */
export function sheetsListQuery(search: SheetsSearch): QueryParams {
  const query: QueryParams = { page: search.page, pageSize: SHEETS_PAGE_SIZE };
  if (search.from) query['from'] = search.from;
  if (search.to) query['to'] = search.to;
  if (search.team) query['teamId'] = search.team;

  switch (search.status) {
    case 'entered':
      query['hasJobs'] = true;
      break;
    case 'incomplete':
      query['incomplete'] = true;
      break;
    case 'missing':
      query['hasJobs'] = false;
      break;
    case 'all':
      break;
  }
  return query;
}

/** J2 `GET /job-days/summary` — the four stat cards. No status dimension. */
export function sheetsSummaryQuery(search: SheetsSearch): QueryParams {
  const query: QueryParams = {};
  if (search.from) query['from'] = search.from;
  if (search.to) query['to'] = search.to;
  if (search.team) query['teamId'] = search.team;
  return query;
}

/** §6.5 — one title/body pair per chip, verbatim. */
export const EMPTY_STATE: Readonly<
  Record<SheetStatusFilter, { title: string; body: string }>
> = {
  all: {
    title: 'No sheets in this period yet',
    body: 'Sheets appear once teams are rostered for a date. Check the period in the top bar.',
  },
  entered: {
    title: 'Nothing has been entered yet',
    body: 'Open a sheet from the list and transcribe its job paper. Every sheet saves itself as you type.',
  },
  incomplete: {
    title: 'Nothing needs attention',
    // ADR-0013 / UAT MD-039 — the third blocker is a team leader, and it is on this list too.
    body: 'Every job worth money has somebody ticked, every overtime pot has somebody who worked it, and every overriding job has a team leader. Payroll can close the period over these.',
  },
  missing: {
    title: 'Every day has an entry',
    body: 'Nothing is waiting. This is what a clean period looks like.',
  },
};

export const FILTER_CHIP_LABEL: Readonly<Record<SheetStatusFilter, string>> = {
  all: 'All',
  entered: 'Entered',
  incomplete: 'Incomplete',
  missing: 'No entry',
};
